import { Component } from "react";
import { IOC_TYPES } from "../../../context/ioc.types";
import { container, lazyInject } from '../../../context/module';
import { Logger } from '../../../models/logger';
import { UploadInProgressFile } from "../../../models/upload-in-progress-file";
import { UIContext } from '../../../services/ui-context';
import type { ConvertOption } from '../../../types/dropdown.type';
import { ConvertOptionCategories, DropdownOption, DropdownWidget, GenericWidget, ICheckboxWidget, InputDropdownWidget, ISeekBarWidget } from '../../../types/dropdown.type';
import { isFunction } from '../../../utils/lodash-min';
import Checkbox from "../../ui/checkbox/checkbox.component";
import Dropdown from '../../ui/dropdown/dropdown';
import InputWidgetComponent from '../../ui/input-widget/input-widget.component';
import InputWithDropdownComponent from '../../ui/input-with-dropdown/input-with-dropdown.component';
import Seekbar from '../../ui/seekbar/seekbar.component';
import { ConvertOptionsContext } from '../convert-options-context';
import styles from './category-option-selections.component.module.scss';
import { withRequestIdleCallback, withRequestIdleCallbackAsync } from "../../../annotations/request-idle-callback.annotation";

export interface CategoryOptionSelectionsProps {
    categoryOption: ConvertOption;
    categoryOptionSelections: DropdownOption[];
    convertOptionsContext: ConvertOptionsContext;
    uploadInProgressFiles?: UploadInProgressFile<any>[];
}

export interface CategoryOptionSelectionsState {
    selectedOption: DropdownOption;
}

export class CategoryOptionSelections extends Component<CategoryOptionSelectionsProps> {
    logger = Logger.getLogger('CategoryOptionSelections');
    static getShownOptionsAndUpdateSelectedOptionIfNeeded(props: CategoryOptionSelectionsProps) {
        var categories: ConvertOptionCategories = props.convertOptionsContext.getConvertOptionCategories();
        var shownOptions = props.categoryOptionSelections.filter(item => !item.shownFn || item.shownFn(categories));
        let selectedOption = shownOptions.find(item => item.selected);
        if (!selectedOption) {
            selectedOption = shownOptions[0];
        }
        props.categoryOptionSelections.forEach(_item => _item.selected = false);
        selectedOption.selected = true;
        return shownOptions;
    }

    static getDerivedStateFromProps(props: CategoryOptionSelectionsProps, state: CategoryOptionSelectionsState) {
        var shownOptions = CategoryOptionSelections.getShownOptionsAndUpdateSelectedOptionIfNeeded(props)
        return { selectedOption: shownOptions.find(item => item.selected), };
    }
    @lazyInject(IOC_TYPES.UIContext) uiContext: UIContext;

    readonly state: Readonly<CategoryOptionSelectionsState> = this.updateSelectedOptionIfNeededAndGetShownOptions(this.props);
    constructor(props: CategoryOptionSelectionsProps) {
        super(props);
    }

    private updateSelectedOptionIfNeededAndGetShownOptions(props: CategoryOptionSelectionsProps) {
        return CategoryOptionSelections.getDerivedStateFromProps(props, this.state);
    }

    private getShownOptionsAndUpdateSelectedOptionIfNeeded(props: CategoryOptionSelectionsProps): DropdownOption[] {
        return CategoryOptionSelections.getShownOptionsAndUpdateSelectedOptionIfNeeded(props);
    }

    private async updateSelectedValue(item: DropdownOption) {
        this.selectItem(item);
        this.setState({
            selectedOption: item,
        });
        this.props.convertOptionsContext.emitEvent({
            selection: item,
            group: this.props.categoryOptionSelections,
        });
        this.updateChildElements(item);
    }

    private selectItem(item: DropdownOption) {
        this.props.categoryOptionSelections.forEach(_item => _item.selected = false);
        item.selected = true;
    }

    private updateChildElements(categoryOptionSelection: DropdownOption) {
        if (categoryOptionSelection.formChildren) {
            categoryOptionSelection.formChildren.forEach(subOption => {
                if (subOption && subOption.displayAs === 'seekbar') {
                    this.setSeekbarValue(subOption.options.value, subOption);
                }
            });
        }
    }

    async canSelect(selectedValue) {
        if (selectedValue?.featureFlags?.canSelect?.fn) {
            const canSelect = await selectedValue.featureFlags.canSelect.fn(container, IOC_TYPES);
            if (!canSelect) {
                this.uiContext.errorToast("Option Unavailable", selectedValue.featureFlags.canSelect.errorMessage);
            }
            return canSelect;
        }
        return true;
    }

    @withRequestIdleCallbackAsync
    async tileItemSelected(item: DropdownOption) {
        const canSelect = await this.canSelect(item);
        if (!canSelect) {
            return;
        }
        await this.updateSelectedValue(item);
        this.setDropdownValue(item, false, this.props.categoryOption);
    }

    setSeekbarValue(value, formChild: GenericWidget<ISeekBarWidget>) {
        formChild.options.value = value;
        if ((formChild as any).selections) {
            (formChild as any).selections.forEach(item => item.selected = false);
            const selectedValue = (formChild as any).selections.find(item => item.formValue === `${value}`);
            if (selectedValue) {
                selectedValue.selected = true;
            }
            this.props.convertOptionsContext.emitEvent({
                formChild: formChild,
                selectedValue: selectedValue,
            });
        }
    }

    @withRequestIdleCallback
    setCheckboxValue(value, formChild: GenericWidget<ICheckboxWidget>) {
        formChild.formValue = value;
        this.props.convertOptionsContext.emitEvent({
            formChild: formChild,
            selectedValue: value,
        });
    }

    getSelectedItemFromSelections(selections) {
        return selections.find(s => s.selected);
    }

    @withRequestIdleCallback
    setDropdownValue(selectedValue: DropdownOption, forced: boolean, formChild: ConvertOption) {
        formChild.selections.forEach(item => item.selected = false);
        if (selectedValue) {
            selectedValue.selected = true;
        } else if (forced) {
            this.logger.error(`Dropdown forced to select a value but there was no selectable values available. 
                This could happen when previously drodown option change to a number input`);
        }
        this.props.convertOptionsContext.emitEvent({
            formChild: formChild,
            selectedValue: selectedValue,
        });
    }

    canShowDropdownValue(typeLessFormGroup, v) {
        if (isFunction(v.shownFn)) {
            return v.shownFn(this.props.convertOptionsContext.getConvertOptionCategories());
        }
        return true;
    }

    @withRequestIdleCallback
    setInputWithDropdownValue(value: { dropdown: any, value: number }, formChild: InputDropdownWidget, dropdownForm: DropdownWidget) {
        dropdownForm.selections.forEach(item => item.selected = false);
        if (value.dropdown) {
            value.dropdown.selected = true;
        }
        formChild.formValue = value;
        const summary = {
            formChild: formChild,
            selectedValue: formChild.formValue,
        };
        this.logger.trace1({ message: 'setInputWithDropdownValue', data: summary });
        this.props.convertOptionsContext.emitEvent(summary);
    }

    @withRequestIdleCallback
    setInputValue(value: number | string, formChild: InputDropdownWidget, dropdownForm: DropdownWidget) {
        formChild.formValue = `${value}`;
        this.props.convertOptionsContext.emitEvent({
            formChild: formChild,
            selectedValue: formChild.formValue,
        });
    }

    componentDidMount() {
        // This was done as a quick fix for options displayed as tiles not being triggered a proper change as it used to be a <Dropdown>
        const shownOptions = this.getShownOptionsAndUpdateSelectedOptionIfNeeded(this.props);
        if (shownOptions.length <= 10) {
            shownOptions.forEach((item, idx) => {
                if (this.state.selectedOption === item) {
                    this.setDropdownValue(item, false, this.props.categoryOption)
                }
            })
        }

        // this.lastLoadedProps = JSONUtils.safeStringify(shownOptions)
    }

    // shouldComponentUpdate(nextProps, nextState) {
    //     const shownOptions = this.getShownOptionsAndUpdateSelectedOptionIfNeeded(this.props);
    //     const nextShownOptions = this.getShownOptionsAndUpdateSelectedOptionIfNeeded(nextProps);
    //     const shouldUpdateComponent = this.lastLoadedProps !== JSONUtils.safeStringify(nextShownOptions)
    //     // console.log(JSON.parse(this.lastLoadedProps), nextShownOptions, shouldUpdateComponent);
    //     return shouldUpdateComponent;
    // }

    // lastLoadedProps: string;

    render() {
        const shownOptions = this.getShownOptionsAndUpdateSelectedOptionIfNeeded(this.props);
        let optionsComp;
        if (shownOptions.length > 10) {
            optionsComp = <Dropdown
                initialValue={this.getSelectedItemFromSelections(shownOptions)}
                values={shownOptions}
                valueFormatter={(v) => v.formDisplayValue}
                hasTooltip={(v) => !!v.tooltip}
                getTooltip={(v) => v.tooltip}
                emitInitialValueOnInit={true}
                shownFn={(v) => this.canShowDropdownValue(null, v)}
                onSelect={(selectedValue, forced) => this.setDropdownValue(selectedValue, forced, this.props.categoryOption)}
                canSelect={(selectedValue) => this.canSelect(selectedValue)}
            >
            </Dropdown>
        } else if (shownOptions.length == 1) {
            optionsComp = null
        } else {
            optionsComp = shownOptions.map((item, idx) => {
                return <div
                    key={`category_option_selections_${idx}`}
                    className={styles['category_option_selection']}
                    data-jkey={`category-option-selections-${idx}`}
                    data-jname={`${item.formName}`}
                >

                    <button className={`btn btn-light btn-gray-outline x-btn-svg x-btn-stacked ${this.state.selectedOption === item && styles['selected']}`}
                        onClick={() => {
                            this.tileItemSelected(item)
                        }}>
                        {
                            item.iconPath && <img height={'18px'} src={item.iconPath} />
                        }
                        {item.formDisplayValue}
                    </button>
                </div>
            })
        }
        let selectedOptionName = this.state.selectedOption.formName;
        let gridStyle = '';
        if (shownOptions.length > 0 && shownOptions.length < 10 && shownOptions.length % 3 == 0) {
            gridStyle = 'three-columns';
        } else if (shownOptions.length > 10) {
            gridStyle = 'one-column';
        }
        return <div className={styles['category_option_selections-and-sub-options']}>
            {
                this.props.categoryOption && this.props.categoryOption.note && <div className={`${this.props.categoryOption.note.bsTextClass} ${styles['category-notes']}`}>
                    {
                        this.props.categoryOption.note.text
                    }
                </div>
            }
            <div className={`${styles['category_option_selections']} ${styles[gridStyle]}`}>
                {
                    optionsComp
                }
            </div>
            {
                this.state.selectedOption && this.state.selectedOption.note && <div
                    className={`${this.state.selectedOption.note.bsTextClass} ${styles['option-notes']}`}
                    dangerouslySetInnerHTML={
                        {
                            __html: this.state.selectedOption.note.textFormatter ?
                                this.state.selectedOption.note.textFormatter(this.state.selectedOption.note.text, this.state.selectedOption)
                                : this.state.selectedOption.note.text
                        }
                    }>

                </div>
            }
            <div className={styles['category_option_selections-sub-options']}
            data-jname={`${this.state.selectedOption.formName}`}>
                {
                    this.state.selectedOption && this.state.selectedOption.formChildren && this.state.selectedOption.formChildren.map((subOption, index) => {
                        let childComponent = subOption.displayAs;
                        let key = `option_${index}`
                        if (subOption && subOption.displayAs === 'seekbar') {
                            key = `seekbar_${selectedOptionName}_option_${index}`
                            childComponent = <Seekbar
                                step={1}
                                min={subOption.options.start}
                                max={subOption.options.end}
                                start={subOption.options.value}
                                emitInitialValueOnInit={true}
                                onChange={(value) => this.setSeekbarValue(value, subOption)}
                            ></Seekbar>;
                        }
                        if (subOption && subOption.displayAs === 'checkbox') {
                            key = `checkbox_${selectedOptionName}_option_${index}`
                            childComponent = <Checkbox
                                ariaLabel={selectedOptionName}
                                initialValue={!!subOption.formValue}
                                emitInitialValueOnInit={true}
                                onChange={(value) => this.setCheckboxValue(value, subOption)}
                            ></Checkbox>;
                        }
                        if (subOption.displayAs === 'dropdown') {
                            key = `dropdown_${selectedOptionName}_option_${index}`
                            childComponent = <Dropdown
                                initialValue={this.getSelectedItemFromSelections(subOption.selections)}
                                values={subOption.selections}
                                valueFormatter={(v) => v.formDisplayValue}
                                hasTooltip={(v) => !!v.tooltip}
                                getTooltip={(v) => v.tooltip}
                                emitInitialValueOnInit={true}
                                shownFn={(v) => this.canShowDropdownValue(subOption, v)}
                                onSelect={(selectedValue, forced) => this.setDropdownValue(selectedValue, forced, subOption)}
                            >
                            </Dropdown>
                        }

                        if (subOption.displayAs === 'input_dropdown') {
                            const initialValue = subOption.formValue && subOption.formValue.input ?
                                Number(subOption.formValue.input) : subOption.defaultValue;
                            const getDisplayValueFn = (v) => {
                                return v && v.formDisplayValue;
                            }
                            key = `input_dropdown_${selectedOptionName}_option_${index}`
                            childComponent = <InputWithDropdownComponent
                                initialValue={this.getSelectedItemFromSelections(subOption.formGroup.selections)}
                                values={subOption.formGroup.selections}
                                shownFn={(value) => !!value}
                                inputValue={initialValue}
                                inputType={'number'}
                                valueFormatter={(v) => getDisplayValueFn(v)}
                                hasTooltip={(v) => !!v.tooltip}
                                getTooltip={(v) => v.tooltip}
                                onValueChange={(value) => this.setInputWithDropdownValue(value, subOption, subOption.formGroup)}
                                emitInitialValueOnInit={true}
                            >
                            </InputWithDropdownComponent>
                        }

                        if (subOption.displayAs === 'input') {
                            const isStringValue = subOption.formValueType == "string"
                            const initialValue = subOption.formValue && subOption.formValue && !isStringValue ?
                                Number(subOption.formValue) : (isStringValue ? subOption.formValue : undefined);
                            key = `input_${selectedOptionName}_option_${index}`
                            childComponent = <InputWidgetComponent
                                inputValue={initialValue}
                                inputType={isStringValue ? 'text' : 'number'}
                                onValueChange={(value) => this.setInputValue(value, subOption, subOption.formGroup)}
                                emitInitialValueOnInit={true}
                            >
                            </InputWidgetComponent>
                        }

                        const selectedChild = subOption.selections?.find(item => item.selected);
                        return <div key={`parent_${selectedOptionName}_option_${index}`}>
                            <div className={styles['category_option_selections-sub-option']} data-jname={`${subOption.formName}`}>
                                <div className={`${styles['label']} text-flex-vertical-center`}>{subOption.formDisplayValue}</div>
                                <div key={key} className={`${styles['controller-container']}`}>
                                    {
                                        childComponent
                                    }
                                </div>
                            </div>
                            {
                                subOption && subOption.note && <div className={`${subOption.note.bsTextClass} ${styles['suboption-notes']}`}
                                    dangerouslySetInnerHTML={{
                                        __html: subOption.note.textFormatter ?
                                            subOption.note.textFormatter(
                                                subOption.note.text,
                                                {
                                                    selectedValue: selectedChild && selectedChild.formValue,
                                                    uploadInProgressFiles: this.props.uploadInProgressFiles
                                                }
                                            )
                                            : subOption.note.text
                                    }}>

                                </div>
                            }
                        </div>
                    })
                }
            </div>
        </div>
    }
}